"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MapPin, CheckCircle } from "lucide-react";
import { useEffect } from "react";

type MobileFiltersBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  neighborhoods: string[];
};

export default function MobileFiltersBottomSheet({
  isOpen,
  onClose,
  neighborhoods,
}: MobileFiltersBottomSheetProps) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const selectedNeighborhood = searchParams.get("neighborhood") || "";
  const selectedRating = searchParams.get("rating") || "";
  const showVerified = searchParams.get("verified") === "true";
  const showFeatured = searchParams.get("featured") === "true";

  // Bloquear scroll do body quando aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const updateParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.push(`/empresas?${params.toString()}`);
  };

  const toggleParam = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (params.get(key) === "true") {
      params.delete(key);
    } else {
      params.set(key, "true");
    }
    params.delete("page");
    router.push(`/empresas?${params.toString()}`);
  };

  const clearFilters = () => {
    router.push("/empresas");
    onClose();
  };

  const hasActiveFilters =
    selectedNeighborhood || selectedRating || showVerified || showFeatured;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 md:hidden"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-999 bg-white rounded-t-3xl shadow-2xl animate-slide-up md:hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-mygreen hover:text-green-700 font-medium"
              >
                Limpar
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Localização */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Bairro
            </h3>
            <select
              value={selectedNeighborhood}
              onChange={(e) => updateParam("neighborhood", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mygreen/20"
            >
              <option value="">Todos os bairros</option>
              {neighborhoods.map((neighborhood) => (
                <option key={neighborhood} value={neighborhood}>
                  {neighborhood}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Status
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer group p-2 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={showVerified}
                  onChange={() => toggleParam("verified")}
                  className="w-4 h-4 text-mygreen rounded focus:ring-mygreen"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Apenas verificadas
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group p-2 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={showFeatured}
                  onChange={() => toggleParam("featured")}
                  className="w-4 h-4 text-mygreen rounded focus:ring-mygreen"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Em destaque
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer com botão aplicar */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-mygreen text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Aplicar Filtros
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
